import { DocumentServicesObject } from '@wix/document-services-types';
import { OpenElementsPanelOptions } from '@wix/editor-platform-sdk-types';

import {
  CheckedState,
  EditorHandlers,
  NormalizedData,
} from '@wix/editor-platform-host-integration-apis';

import { INIT_NORMALIZED_DATA_STATE } from './constants';

import {
  getPrimaryConnectionsWithRefs,
  getFullNormalizedItem,
  setCompRefsAndCheckedState,
  handleDefaultItemCase,
  handleItemWithCategoryId,
  handleItemWithSectionId,
  getIdentifierFromRoleAndSubRole,
  getCompRefByRole,
  updateDescriptors,
  sortNormalizedItems,
  addSectionsItemsData,
} from './utils';
import {
  ElementsPanelProps,
  EditorPointerServiceAPI,
} from '@wix/editor-platform-feature-store';

export const getNativeElementsPanelAPI = (
  documentServicesAPI: DocumentServicesObject,
  options: OpenElementsPanelOptions,
  editorHandlers: EditorHandlers,
  editorPointerAPI?: EditorPointerServiceAPI,
): ElementsPanelProps | null => {
  const primaryConnectionsWithRefs = getPrimaryConnectionsWithRefs(
    documentServicesAPI,
    options.widgetRef,
  );

  const {
    data: { checkedState, compRefsMap },
    ...items
  } = options.elementsData.reduce<NormalizedData>((acc, item) => {
    const fullItem = getFullNormalizedItem(
      documentServicesAPI,
      primaryConnectionsWithRefs,
      item,
    );

    acc.data = setCompRefsAndCheckedState(acc.data, fullItem);

    if (item.sectionId) {
      const accumulatorWithSectionData = addSectionsItemsData(
        item.sectionId!,
        acc,
        options.sectionsData,
      );

      return handleItemWithSectionId(
        fullItem,
        accumulatorWithSectionData,
        options,
      );
    }

    if (item.categoryId) {
      return handleItemWithCategoryId(fullItem, acc, options);
    }

    return handleDefaultItemCase(fullItem, acc);
  }, INIT_NORMALIZED_DATA_STATE);

  const sortedItems =
    sortNormalizedItems(items).notInSectionItems.categoriesWithItems;

  const sortedFlatList = Object.values(sortedItems).flatMap(
    (el) => el.children,
  );

  const itemForPanel: ElementsPanelProps['items'] = sortedFlatList.map(
    (item) => ({
      displayName: item.displayName ?? item.label ?? '',
      id: getIdentifierFromRoleAndSubRole(item.identifier),
      isRemovable: true,
      tooltipData: item.tooltipData?.content,
    }),
  );

  const getFullNormalizedItemById = (id: string) =>
    sortedFlatList.find(
      (element) => getIdentifierFromRoleAndSubRole(element.identifier) === id,
    );

  const getCompRefById = (id: string) => {
    if (compRefsMap[id]) {
      return compRefsMap[id];
    }

    const item = getFullNormalizedItemById(id);
    if (!item) {
      return null;
    }

    return getCompRefByRole(
      documentServicesAPI,
      item.identifier,
      options.widgetRef,
    );
  };

  const handleToggleState = async (
    id: string,
    isToggled: boolean,
  ): Promise<CheckedState> => {
    const compRef = getCompRefById(id);
    const item = getFullNormalizedItemById(id);

    if (!compRef || !item) {
      return {};
    }

    const descriptors = isToggled
      ? options.addComponentHandler(item.identifier, compRef)
      : options.removeComponentHandler(compRef, item.identifier);

    const updatedDescriptors = await updateDescriptors(descriptors, isToggled);

    const result = {
      [id]: isToggled,
      ...updatedDescriptors,
    };

    if (!isToggled) {
      const editorPointer = editorPointerAPI?.buildEditorPointer(compRef);

      editorPointer && editorHandlers.removeHighlight(editorPointer);
    }

    return result;
  };

  const handleHighlight =
    (handler: EditorHandlers[keyof EditorHandlers]) => (id: string) => {
      const compRef = getCompRefById(id);

      if (!compRef) {
        return;
      }

      const editorPointer = editorPointerAPI?.buildEditorPointer(compRef);

      editorPointer && handler(editorPointer);
    };

  return {
    data: checkedState,
    items: itemForPanel,
    subtitle: options.subtitle,
    onToggleState: handleToggleState,
    highlightComponent: handleHighlight(editorHandlers.highlightComponent),
    removeHighlight: handleHighlight(editorHandlers.removeHighlight),
  };
};
